/**
 * The Fitment API client, as supplied (temp/fitment.js, 2026-10-10).
 * Loaded on product pages for part-diagram.js. Do not edit the flow here;
 * the API's own write-up (temp/shopify-app.txt) describes the token and the
 * proxy fallback it relies on.
 */
/* Reference client for the Shopify Fitment API — drop into a theme section or
 * a theme app extension. No framework, no dependencies.
 *
 * Flow (docs/shopify-fitment-api-plan.md):
 *   1. once per visit:  GET /apps/fitment/token   (through Shopify's proxy → signed)
 *      → { token, expires_in, api_base, brands }   kept in sessionStorage
 *   2. then direct calls to api_base with  Authorization: Bearer <token>
 *      — if a direct call fails (blocked, expired, CORS), fall back to the
 *        proxy path, which serves every op too.
 *
 * The three calls a product page needs:
 *   Fitment.check(brand, model, sku)    → {found, assemblies, with_drawing, ...}   show the button?
 *   Fitment.fitment(brand, model, sku)  → {machine, part, assemblies:[...]}       the list
 *   Fitment.diagram(brand, ipl_key, sku) → the drawing, parts, hotspots, highlight[]
 */
(function (global) {
  "use strict";

  const PROXY = "/apps/fitment";
  const STORE_KEY = "fitment.session";

  function readSession() {
    try {
      const s = JSON.parse(sessionStorage.getItem(STORE_KEY) || "null");
      return s && s.expires_at > Date.now() + 30000 ? s : null;
    } catch (e) { return null; }
  }

  async function session() {
    const cached = readSession();
    if (cached) return cached;
    const r = await fetch(PROXY + "/token", { credentials: "same-origin" });
    if (!r.ok) throw new Error("fitment: no token (" + r.status + ")");
    const body = await r.json();
    const s = { token: body.token, api_base: body.api_base, brands: body.brands,
                expires_at: Date.now() + body.expires_in * 1000 };
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(s)); } catch (e) { /* private mode */ }
    return s;
  }

  function qs(params) {
    const out = new URLSearchParams();
    Object.keys(params).forEach(k => { if (params[k] != null && params[k] !== "") out.set(k, params[k]); });
    return out.toString();
  }

  async function call(op, params, body) {
    const s = await session();
    const query = qs(params);
    const init = { method: body ? "POST" : "GET", headers: { Authorization: "Bearer " + s.token } };
    if (body) { init.headers["Content-Type"] = "application/json"; init.body = JSON.stringify(body); }
    try {
      const r = await fetch(s.api_base + "/v1/" + op + (query ? "?" + query : ""), init);
      if (r.status === 404 && !r.headers.get("content-type")) throw new Error("direct refused");
      if (r.status === 429) { await new Promise(res => setTimeout(res, 1000 * (+r.headers.get("Retry-After") || 1))); return call(op, params, body); }
      return await r.json();
    } catch (e) {
      // Fallback: through Shopify. Every selector is a query parameter and
      // `op` must match the path — both are inside Shopify's signature.
      const r = await fetch(PROXY + "/v1/" + op + "?" + qs(Object.assign({ op: op }, params)),
        body ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {});
      if (!r.ok && r.status !== 404 && r.status !== 400) throw e;
      return await r.json();
    }
  }

  global.Fitment = {
    brands: () => call("brands", {}),
    tree: (brand, node) => call("tree", { brand, node }),
    model: (brand, model) => call("model", { brand, model }),
    diagram: (brand, diagram, sku) => call("diagram", { brand, diagram, sku }),
    part: (brand, sku) => call("part", { brand, sku }),
    fitment: (brand, model, sku) => call("fitment", { brand, model, sku }),
    check: (brand, model, sku) => call("check", { brand, model, sku }),
    checkMany: (brand, model, skus) => call("check", {}, { brand, model, skus }),
    resolve: (brand, q) => call("resolve", { brand, q }),
  };
})(window);
